import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  StorageLocationsOverviewParams,
  StorageLocationsResponse,
} from 'src/apis/storage/types';
import type { StorageServiceRootRequest } from 'src/apis/storage/use-storage-service-root';
import { useAxios, type Successful } from 'src/hooks/use-axios';
import { NotFound } from 'src/utils/errors';

type HookProps = {
  storageServiceRoot: Successful<StorageServiceRootRequest>;
  params: StorageLocationsOverviewParams;
};

type ExecuteProps = {
  url: string;
  params: StorageLocationsOverviewParams;
};

export function usePublicStorageLocations({
  storageServiceRoot,
  params,
}: HookProps) {
  const storageLocations = useAxios(
    (axios, baseConfig, { url, params }: ExecuteProps) =>
      axios.request<StorageLocationsResponse>({ ...baseConfig, url, params }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!storageServiceRoot.response.data._links!.getLocationsOverviewPublic) {
      throw new NotFound();
    }

    const locationsUrl =
      storageServiceRoot.response.data._links?.getLocationsOverview ||
      storageServiceRoot.response.data._links!.getLocationsOverviewPublic;

    const templatedUrl = urlTemplate.parse(locationsUrl.href).expand({});

    storageLocations.execute({
      url: templatedUrl,
      params,
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [storageServiceRoot.response, params]);

  return storageLocations;
}

export type PublicStorageLocationsRequest = ReturnType<
  typeof usePublicStorageLocations
>;
